import {css, useTheme} from 'styled-components';

import {CSSProperties, KeyboardEventHandler, MouseEventHandler, Suspense} from 'react';

import {isNotNil} from 'ramda-adjunct';

import {suffixTestId, TestIdProps} from 'shared/web';

import {useResponsivePropValue} from '../../hooks/useResponsivePropValue';
import {Integer} from '../../types/Integer';
import {ValueByDevice} from '../../types/ValueByDevice';
import {ThemeColorPath, ThemeIconKey} from '../../utils/foundationTheme';
import {getColorByPath} from '../../utils/getColorByPath';
import {IconErrorBoundary} from './IconErrorBoundary';

export interface IconProps extends TestIdProps {
  value: ThemeIconKey | undefined;
  color?: ThemeColorPath;
  cursor?: CSSProperties['cursor'];
  size?: Integer | ValueByDevice<Integer>;
  isNotFilled?: boolean;
  tabIndex?: number;
  onClick?: MouseEventHandler<HTMLDivElement>;
  onKeyDown?: KeyboardEventHandler<HTMLDivElement>;
}

export function Icon(props: IconProps) {
  const theme = useTheme();
  const size = useResponsivePropValue(props.size ?? 6);
  const cssSize = theme.getSize(size);

  if (!props.value) {
    return null;
  }

  const IconComponent = theme.icons?.[props.value];
  const FallbackIconComponent = theme.icons.fallback;

  const color = props.color ? getColorByPath(props.color, theme) : 'currentColor';

  return (
    <div
      role="img"
      css={css`
        cursor: ${props.cursor ?? (props.onClick ? 'pointer' : undefined)};
        color: ${color};
        line-height: 0;
        transition: box-shadow 250ms ease-in-out; /* For focus-visible */
        border-radius: 2px; /* For focus-visible */
        svg {
          width: ${cssSize};
          height: ${cssSize};
        }
        svg:not([data-iconkey^='flags/']):not([data-iconkey^='flagsCurrencies/']):not(
            [data-iconkey^='files_compact/']
          ):not([data-iconkey^='files_default/']):not([data-iconkey^='condition/']):not(
            [data-iconkey^='automotive/']
          ) {
          ${!props.isNotFilled ? 'path {fill: currentColor}' : ''}
        }
        &:focus-visible {
          outline: none;
          box-shadow: 0 0 0 2px;
        }
      `}
      onClick={props.onClick}
      onKeyDown={props.onKeyDown}
      tabIndex={props.tabIndex}
      data-iconkey={props.value}
      data-testid={suffixTestId('icon', props)}
    >
      <IconErrorBoundary icon={props.value} fallback={<FallbackIconComponent />}>
        <Suspense fallback={<FallbackIconComponent />}>
          {isNotNil(IconComponent) && <IconComponent data-iconkey={props.value} />}
        </Suspense>
      </IconErrorBoundary>
    </div>
  );
}
